import { IconBrandAppleFilled, IconBrandAndroid, IconCircleCheckFilled } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { usePromoCode, getPromoWithNearestExpiry } from "@/hooks/use-promo-code";

import YouTubePlayer from "../common/YouTubePlayer";

const ProductIntro = () => {
  // @ts-ignore - avoid deep instantiation from i18next types
  const { t } = useTranslation();
  const { eventCode, eventData, referralCode, referralData } = usePromoCode();
  const features = t('productIntro.features', { returnObjects: true }) as string[];

  const countdownData = getPromoWithNearestExpiry(eventData, referralData);
  const codeForDescription = eventCode || referralCode;

  const getDescription = (): string | null => {
    const code = codeForDescription || 'WEBSITE';
    if (countdownData?.expires_at && Date.now() / 1000 > countdownData.expires_at) {
      return null;
    }

    const promoKey = `promo.${code}.description`;
    const translation = t(promoKey) as string;

    // If translation doesn't exist (returns the key itself), return null
    if (translation === promoKey) {
      return null;
    }

    return translation || null;
  };

  const description = getDescription();
  const videos = t('productIntro.videos', { returnObjects: true }) as { url: string; youtubeUrl: string; cover: string }[];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-4 pt-6 md:pt-8 pb-12 sm:px-6 lg:px-8">
      <div className="px-4 md:px-4">

        {description && (
          <div className="bg-red-100/80 text-base text-red-950 rounded-lg px-6 py-4 mb-12 leading-relaxed border border-red-200/50">
            <p className="whitespace-pre-line">
              {description}
            </p>
          </div>
        )}

        <h2 className="text-xl md:text-2xl font-semibold py-0 leading-none md:leading-none mb-4 md:mb-8 border-l-4 border-sky-500 pl-4">
          {t('productIntro.title')}
        </h2>

        {t('productIntro.opening') != "productIntro.opening" &&
          <p className="mb-8 text-slate-800 text-base md:text-lg leading-relaxed">
            {t('productIntro.opening')}
          </p>
        }

        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-8 bg-slate-100 py-4 px-8 md:px-6 md:rounded-lg -mx-8 md:-mx-1">
          <span className="text-slate-400 text-sm">{t('productIntro.platforms.title')}</span>
          <div className="flex items-center gap-2 text-sm">
            <IconBrandAppleFilled className="w-5 h-5 text-slate-700" />
            <span className="text-slate-700 font-semibold">{t('productIntro.platforms.iphone')}</span>
          </div>
          <div className="hidden md:block w-[1px] bg-slate-200 h-6 mx-1"></div>
          <div className="flex items-center gap-2 text-sm">
            <IconBrandAndroid className="w-5 h-5 text-slate-300" />
            <span className="text-slate-300">{t('productIntro.platforms.android')}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4">
          {features.map((item, index) => (
            <div key={index} className={`flex items-center gap-3`}>
              <IconCircleCheckFilled className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-8 pb-8 md:pt-10 md:pb-10 px-3">
        <div className="h-px bg-slate-200"></div>
      </div>

      <div className="text-slate-800 text-base md:text-lg px-4 md:px-4">
        {(t('productIntro.description', { returnObjects: true }) as string[]).map((paragraph, index) => (
          <p key={index} className="mb-5 leading-relaxed">
            {paragraph}
          </p>
        ))}
      </div>

      {videos && videos.length > 0 && (
        <div className="mt-6 md:mt-8 flex flex-row gap-2">
          {videos.map((video, index) => (
            <div
              key={index}
              className={`aspect-[3/4] w-full ${index > 0 ? 'hidden md:block' : ''}`}
            >
              <YouTubePlayer
                youtubeUrl={video.youtubeUrl}
                poster={video.cover}
                objectFit="contain"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ProductIntro